import { useEffect, useRef, useState } from 'react'

interface SpeechMessage {
  text: string
  authorId: string
}

export function useSpeech() {
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([])
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const queueRef = useRef<SpeechSynthesisUtterance[]>([])
  const currentUtteranceIndexRef = useRef<number>(0)
  const shouldContinueRef = useRef<boolean>(true)

  useEffect(() => {
    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices()
      // Filter to English voices
      const englishVoices = availableVoices.filter((voice) =>
        voice.lang.startsWith('en')
      )
      setVoices(englishVoices)
    }

    // Load voices immediately
    loadVoices()

    // Some browsers load voices asynchronously
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices
    }

    // Cleanup on unmount
    return () => {
      window.speechSynthesis.cancel()
      queueRef.current = []
    }
  }, [])

  const speakMessages = (messages: SpeechMessage[]) => {
    if (!('speechSynthesis' in window)) {
      console.warn('Speech synthesis not supported in this browser')
      return
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel()
    queueRef.current = []
    currentUtteranceIndexRef.current = 0
    shouldContinueRef.current = true
    setIsPaused(false)

    if (messages.length === 0) {
      setIsSpeaking(false)
      return
    }

    // Get two different voices for alternating
    const [voiceA, voiceB] =
      voices.length >= 2 ? [voices[0], voices[1]] : [voices[0], voices[0]]

    const utterances: SpeechSynthesisUtterance[] = []

    messages.forEach((msg, index) => {
      const utterance = new SpeechSynthesisUtterance(msg.text)

      // Alternate voices for each comment (switch on each new comment)
      utterance.voice = index % 2 === 0 ? voiceA : voiceB

      utterance.rate = 1.0
      utterance.pitch = 1.0
      utterance.volume = 1.0

      utterances.push(utterance)
    })

    queueRef.current = utterances

    // Speak utterances sequentially by chaining onend events
    const speakNext = (index: number) => {
      // Check if we should continue (stop was called)
      if (!shouldContinueRef.current) {
        return
      }

      if (index >= utterances.length) {
        setIsSpeaking(false)
        setIsPaused(false)
        queueRef.current = []
        shouldContinueRef.current = true
        return
      }

      const utterance = utterances[index]
      currentUtteranceIndexRef.current = index

      utterance.onstart = () => {
        if (!shouldContinueRef.current) {
          window.speechSynthesis.cancel()
          return
        }
        setIsSpeaking(true)
        setIsPaused(false)
      }

      utterance.onend = () => {
        // Only continue if stop wasn't called
        if (shouldContinueRef.current) {
          speakNext(index + 1)
        }
      }

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event)
        // Only continue if stop wasn't called
        if (shouldContinueRef.current) {
          speakNext(index + 1)
        }
      }

      if (shouldContinueRef.current) {
        window.speechSynthesis.speak(utterance)
      }
    }

    // Start with first utterance
    speakNext(0)
  }

  const pauseSpeaking = () => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause()
      setIsPaused(true)
    }
  }

  const resumeSpeaking = () => {
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume()
      setIsPaused(false)
    }
  }

  const stopSpeaking = () => {
    // Set flag to prevent continuation
    shouldContinueRef.current = false
    
    // Cancel all speech immediately
    window.speechSynthesis.cancel()
    
    // Clear all utterance event handlers to prevent callbacks
    queueRef.current.forEach((utterance) => {
      utterance.onstart = null
      utterance.onend = null
      utterance.onerror = null
    })
    
    // Reset state
    queueRef.current = []
    currentUtteranceIndexRef.current = 0
    setIsSpeaking(false)
    setIsPaused(false)
    
    // Reset flag for next playback
    shouldContinueRef.current = true
  }

  const isSupported = 'speechSynthesis' in window

  return {
    speakMessages,
    pauseSpeaking,
    resumeSpeaking,
    stopSpeaking,
    isSpeaking,
    isPaused,
    isSupported,
    voices,
  }
}

